import React, { useContext, useState } from "react";
import { Button, Card, FormLayout, Icon, Layout, Link, Modal, PageActions, Stack, TextContainer, TextField, TextStyle } from "@shopify/polaris";
import { CircleTickMajor, HideMinor, ViewMinor } from "@shopify/polaris-icons";

import { useMutation } from "@apollo/react-hooks";
// import hoc
import { withErrorBoundary } from "lib/hoc";

import { PublicContext } from "lib/context";
import constant from "lib/constant/constant";
import { Banner, Spinner, PasswordRequirements } from "lib/components";
import { baseHelper, errorHelper } from "lib/helpers";

import { CHECK_RESET_TOKEN, UPDATE_PASSWORD } from "app/public/apollo/mutations";
import validate from "./yup";

const UpdatePassword = () => {
  const { history, cms, match } = useContext(PublicContext);
  const { params } = match || {};
  const { token } = params || {};
  const [checkResetToken, { loading: checkTokenLoading = false }] = useMutation(CHECK_RESET_TOKEN);
  const [resetPassword, { loading: resetPasswordLoading }] = useMutation(UPDATE_PASSWORD);
  const [isLoading, setIsLoading] = useState(true);
  const [values, setValues] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [visiblePassword, setVisiblePassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [isReset, setIsReset] = useState(false);
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const { CONFIRM_PASSWORD, CRITICAL, gql, NEW_PASSWORD, PASSWORD, SUCCESS } = constant;
  const [errorMessage, setErrorMessage] = useState(true);
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const handleValidation = async (field, value) => {
    const validationError = await validate(field, value, cms);
    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
    setIsReset(!validationError);
  };
  const handleChange = (key, value) => {
    setValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };
  const togglePasswordVisibility = (key) => {
    setVisiblePassword((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };
  const renderViewIcon = (key, showLabel, hideLabel) => (
    <Button
      id="eyeButton"
      plain
      icon={visiblePassword[key] ? HideMinor : ViewMinor}
      accessibilityLabel={visiblePassword[key] ? hideLabel : showLabel}
      onClick={() => togglePasswordVisibility(key)}
    />
  );
  const checkToken = async () => {
    if (isLoading) {
      try {
        const res = await checkResetToken({ variables: { input: { token } } });
        const resData = baseHelper.getResponseData(res.data, gql.CHECK_RESET_TOKEN);
        if (!resData) {
          const error = baseHelper.getResponseError(res.data, gql.CHECK_RESET_TOKEN);
          setBanner({
            status: CRITICAL,
            isOpen: true,
            title: error,
          });
        }
      } catch (exception) {
        setBanner({
          isOpen: true,
          status: CRITICAL,
          title: errorHelper.parse(exception),
        });
      }
      setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    await handleValidation(NEW_PASSWORD, values.newPassword);
    await handleValidation(CONFIRM_PASSWORD, {
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    });

    if (!isReset) {
      return;
    }
    const isAnyValidationError = () => Object.values(errorMessage).some((error) => error);
    const isAllValuesFilled = () => Object.values(values).every((value) => value);
    setIsReset(isAllValuesFilled && !isAnyValidationError);
    if (!isAnyValidationError() && isAllValuesFilled()) {
      try {
        const res = await resetPassword({
          variables: {
            input: { newPassword: values.newPassword, token },
          },
        });
        const resData = baseHelper.getResponseData(res.data, gql.RESET_PASSWORD);
        let bannerValue = {
          status: SUCCESS,
          title: cms("message.success"),
        };
        let isResetPassword = true;
        if (!resData) {
          isResetPassword = false;
          const error = baseHelper.getResponseError(res.data, gql.RESET_PASSWORD);
          bannerValue = { status: CRITICAL, title: error };
        }
        setBanner({
          status: bannerValue.status,
          isOpen: true,
          title: bannerValue.title,
        });
        if (isResetPassword) {
          setIsResetSuccess(true);
        }
      } catch (exception) {
        setBanner({
          isOpen: true,
          status: CRITICAL,
          title: errorHelper.parse(exception),
        });
      }
    }
  };
  if (isLoading) {
    checkToken();
    setIsLoading(false);
  }

  return (
    <>
      {checkTokenLoading && <Spinner />}
      <Banner title={banner.title} status={banner.status} isOpen={banner.isOpen} />
      <br />
      <Layout.AnnotatedSection
        title="Welcome to MarketCube"
        description="Create a secure password to complete your account setup"
      >
          <Card>
            <Card.Section>
              <FormLayout>
                <TextField
                  id="newPassword"
                  label={`${cms("label.newPassword")}*`}
                  type={visiblePassword.newPassword ? "text" : PASSWORD}
                  value={values.newPassword || ""}
                  placeholder={cms("placeholder.newPassword")}
                  onChange={(value) => {
                    setIsPasswordTouched(true);
                    handleChange(NEW_PASSWORD, value);
                  }}
                  onBlur={() => {
                    setIsPasswordTouched(true);
                    return !values.confirmPassword
                      ? handleValidation(NEW_PASSWORD, values.newPassword)
                      : (handleValidation(NEW_PASSWORD, values.newPassword),
                        handleValidation(CONFIRM_PASSWORD, {
                          newPassword: values.newPassword,
                          confirmPassword: values.confirmPassword,
                        }));
                  }} 
                  error={errorMessage && errorMessage.newPassword}
                  suffix={renderViewIcon("newPassword", "Show new password", "Hide new password")}
                />
                <TextField
                  id="confirmPassword"
                  label={`${cms("label.confirmNewPassword")}*`}
                  placeholder={cms("placeholder.confirmNewPassword")}
                  type={visiblePassword.confirmPassword ? "text" : PASSWORD}
                  value={values.confirmPassword || ""}
                  onChange={(value) => handleChange(CONFIRM_PASSWORD, value)}
                  onBlur={() => {
                    handleValidation(CONFIRM_PASSWORD, {
                      newPassword: values.newPassword,
                      confirmPassword: values.confirmPassword,
                    });
                  }}
                  error={errorMessage && errorMessage.confirmPassword}
                  suffix={renderViewIcon("confirmPassword", "Show confirm password", "Hide confirm password")}
                />
                <PasswordRequirements password={values.newPassword} isTouched={isPasswordTouched} />
              </FormLayout>
            </Card.Section>
          </Card>
          <PageActions
            primaryAction={{
              id: "resetPassword",
              content: cms("button.primary"),
              disabled: !isReset,
              onAction: onSubmit,
              loading: resetPasswordLoading,
            }}
          />
        </Layout.AnnotatedSection>
      <Modal
        open={isResetSuccess}
        onClose={() => history.push("/login")}
        title="Password Reset Successful"
        primaryAction={{
          content: "Return to Login",
          onAction: () => history.push("/login"),
        }}
      >
        <Modal.Section>
          <Stack vertical spacing="loose" alignment="center">
            <Icon source={CircleTickMajor} color="greenDark" backdrop />
            <TextContainer>
              <TextStyle variation="subdued">
                Your password has been reset successfully. You can now log in to your MarketCube Dashboard using your
                new password.
              </TextStyle>
            </TextContainer>
            <Stack spacing="tight" alignment="center">
              <TextStyle variation="subdued">Need help?</TextStyle>
              <Link url="mailto:support@marketcube.io">Contact Support</Link>
            </Stack>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default withErrorBoundary(UpdatePassword);
